import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import "./i18n";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AdminLayout } from "./components/AdminLayout";
import InstallApp from "./components/InstallApp";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AccountSetup from "./pages/AccountSetup";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Dashboard from "./pages/admin/Dashboard";
import Fleet from "./pages/admin/Fleet";
import Bookings from "./pages/admin/Bookings";
import Tracking from "./pages/admin/Tracking";
import Workshop from "./pages/admin/Workshop";
import Customers from "./pages/admin/Customers";
import Finance from "./pages/admin/Finance";
import Contracts from "./pages/admin/Contracts";
import Multe from "./pages/admin/Multe";
import Fatture from "./pages/admin/Fatture";
import Expenses from "./pages/admin/Expenses";
import UsersPage from "./pages/admin/Users";
import SharedBlacklist from "./pages/admin/SharedBlacklist";
import Damages from "./pages/admin/Damages";
import Sinistri from "./pages/admin/Sinistri";
import Alerts from "./pages/admin/Alerts";
import Reports from "./pages/admin/Reports";
import Settings from "./pages/admin/Settings";
import AIAssistant from "./pages/admin/AIAssistant";
import Commercialista from "./pages/admin/Commercialista";
import Oracle from "./pages/admin/Oracle";
import MobileApp from "./pages/admin/MobileApp";
import Sedi from "./pages/admin/Sedi";
import Plans from "./pages/admin/Plans";
import Integrations from "./pages/admin/Integrations";
import Assicurazioni from "./pages/admin/Assicurazioni";
import Tariffe from "./pages/admin/Tariffe";
import Planning from "./pages/admin/Planning";
import TCO from "./pages/admin/TCO";
import VisionHub from "./pages/admin/VisionHub";
import Dunning from "./pages/admin/Dunning";
import Transfers from "./pages/admin/Transfers";
import Channels from "./pages/admin/Channels";
import IoT from "./pages/admin/IoT";
import ContractSign from "./pages/ContractSign";
import ExternalRedirect from "./components/ExternalRedirect";
import PecInbox from "./pages/admin/PecInbox";
import Rinotifiche from "./pages/admin/Rinotifiche";
import SumupPayments from "./pages/admin/SumupPayments";
import StoragePage from "./pages/admin/Storage";
import Compliance from "./pages/admin/Compliance";
import SystemHealth from "./pages/admin/SystemHealth";
import CustomerInsights from "./pages/admin/CustomerInsights";
import PredictiveMaintenance from "./pages/admin/PredictiveMaintenance";
import SosDashboard from "./pages/admin/SosDashboard";
import Marketplace from "./pages/admin/Marketplace";
import IntelligentSuite from "./pages/admin/IntelligentSuite";
import EioDashboard from "./pages/admin/EioDashboard";
import SuperAdmin from "./pages/admin/SuperAdmin";
import WhatsAppPage from "./pages/admin/WhatsApp";
import YardOperator from "./pages/yard/YardOperator";
import AuthCallback from "./pages/AuthCallback";

const Private = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready && !user) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#050505", color: "#fff" }}>Caricamento…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  // Setup account pendente → pagina dedicata (NON login)
  if (user.must_change_password) return <Navigate to="/setup" replace />;
  if (user.two_factor_required && !user.two_factor_enabled) return <Navigate to="/setup" replace />;
  if (!ready && (user.must_change_password === undefined || user.two_factor_enabled === undefined)) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#050505", color: "#fff" }}>Caricamento…</div>;
  }
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // Emergent Google Auth callback: if URL fragment has session_id, render AuthCallback FIRST.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/setup" element={<AccountSetup />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
      <Route path="/sign/:id" element={<ContractSign />} />
      {/* Prenotazioni pubbliche ritirate dal gestionale: redirect al sito ufficiale */}
      <Route path="/noleggio" element={<ExternalRedirect to="https://fabri-rent.it" />} />
      <Route path="/noleggio/*" element={<ExternalRedirect to="https://fabri-rent.it" />} />
      <Route path="/checkout" element={<ExternalRedirect to="https://fabri-rent.it" />} />
      <Route path="/checkout/*" element={<ExternalRedirect to="https://fabri-rent.it" />} />
      <Route path="/prenota" element={<ExternalRedirect to="https://fabri-rent.it" />} />
      <Route path="/prenota/*" element={<ExternalRedirect to="https://fabri-rent.it" />} />
      <Route path="/yard" element={<YardOperator />} />
      <Route path="/admin" element={<Private><AdminLayout /></Private>}>
            <Route index element={<Dashboard />} />
            <Route path="fleet" element={<Fleet />} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="contracts" element={<Contracts />} />
            <Route path="multe" element={<Multe />} />
            <Route path="pec-inbox" element={<PecInbox />} />
            <Route path="rinotifiche" element={<Rinotifiche />} />
            <Route path="sumup" element={<SumupPayments />} />
            <Route path="storage" element={<StoragePage />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="system-health" element={<SystemHealth />} />
            <Route path="customers/:id/insights" element={<CustomerInsights />} />
            <Route path="predictive" element={<PredictiveMaintenance />} />
            <Route path="sos" element={<SosDashboard />} />
            <Route path="marketplace" element={<Marketplace />} />
            <Route path="intelligent-suite" element={<IntelligentSuite />} />
            <Route path="eio" element={<EioDashboard />} />
            <Route path="superadmin" element={<SuperAdmin />} />
            <Route path="whatsapp" element={<WhatsAppPage />} />
            <Route path="fatture" element={<Fatture />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="damages" element={<Damages />} />
            <Route path="sinistri" element={<Sinistri />} />
            <Route path="shared-blacklist" element={<SharedBlacklist />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="reports" element={<Reports />} />
            <Route path="sedi" element={<Sedi />} />
            <Route path="plans" element={<Plans />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="tracking" element={<Tracking />} />
            <Route path="workshop" element={<Workshop />} />
            <Route path="customers" element={<Customers />} />
            <Route path="finance" element={<Finance />} />
            <Route path="settings" element={<Settings />} />
            <Route path="ai" element={<AIAssistant />} />
            <Route path="commercialista" element={<Commercialista />} />
            <Route path="oracle" element={<Oracle />} />
            <Route path="mobile-app" element={<MobileApp />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="assicurazioni" element={<Assicurazioni />} />
            <Route path="tariffe" element={<Tariffe />} />
            <Route path="planning" element={<Planning />} />
            <Route path="tco" element={<TCO />} />
            <Route path="vision" element={<VisionHub />} />
            <Route path="dunning" element={<Dunning />} />
            <Route path="transfers" element={<Transfers />} />
            <Route path="channels" element={<Channels />} />
            <Route path="iot" element={<IoT />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <InstallApp />
        <Toaster position="top-right" theme="dark" toastOptions={{ style: { background: "#0a0a0a", border: "1px solid #ff0033", color: "white" } }} />
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
